import { AuthContext, FireactContext, SetPageTitle } from "../lib/core";
import { Box, Button, Container, Grid, Typography, Divider, Paper, Link } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../template/Footer";
import VerifiedIcon from '@mui/icons-material/VerifiedUser';
import "./About.css";

const Privacy = ({loader}) => {

    const { authInstance, authUser } = useContext(AuthContext);
    const { config } = useContext(FireactContext);
    const navigate = useNavigate();
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        setLoaded(false);
        setLoaded(true);
    },[authInstance, config.saas.permissions]);

    return (
        <>
            {loaded ? (
                <Container maxWidth={false} disableGutters>
                    <SetPageTitle title="Privacy Policy" />

                    <Box className="about-hero">
                        <Container>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={7}>
                                    <Typography variant="h2" className="hero-title">Privacy Policy</Typography>
                                    <Typography variant="h6" color="text.secondary" className="hero-sub">
                                        Last updated: January 1, 2025
                                    </Typography>
                                    <Box sx={{ mt: 3 }}>
                                        <Button variant="outlined" color="secondary" onClick={() => navigate('/contact')}>Contact Us</Button>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <Box className="hero-art">
                                        <VerifiedIcon sx={{ fontSize: 120, color: 'primary.main' }} />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Container>
                    </Box>

                    <Container sx={{ py: 6 }}>
                        <Paper elevation={3} sx={{ p: { xs:2, md:4 }, borderRadius: 3 }}>
                            <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>1. Introduction</Typography>
                            <Typography variant="body1" paragraph>
                                This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our mobile application and related services (“App”). By using the App, you agree to the practices described in this Policy.
                            </Typography>
                            <Typography variant="body1" paragraph>
                                We follow all requirements outlined in the Apple App Store Review Guidelines, including transparency around data collection, data usage, and user rights.
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>2. Information We Collect</Typography>
                            <Typography variant="subtitle1">2.1 Information You Provide</Typography>
                            <Typography variant="body1" paragraph>
                                We may collect the following information when you interact with the App:
                            </Typography>
                            <ul>
                                <li>Account information (such as name, email address, or profile details)</li>
                                <li>Information you submit through forms or support requests</li>
                                <li>Any content you upload or share within the App</li>
                            </ul>

                            <Typography variant="subtitle1">2.2 Automatically Collected Information</Typography>
                            <Typography variant="body1" paragraph>
                                When you use the App, we may automatically collect:
                            </Typography>
                            <ul>
                                <li>Device information (model, OS version, device identifiers not tied to identity)</li>
                                <li>App usage data (features used, interactions, performance logs)</li>
                                <li>Crash data and diagnostics</li>
                                <li>Approximate location (only if required for core functionality and permitted by you)</li>
                            </ul>

                            <Typography variant="subtitle1">2.3 No Sensitive Data Without Consent</Typography>
                            <Typography variant="body1" paragraph>
                                We do not collect sensitive personal data (such as health, financial, or biometric data) unless explicitly required for a feature and only with your clear permission.
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>3. How We Use Your Information</Typography>
                            <Typography variant="body1" paragraph>
                                We use your information to:
                            </Typography>
                            <ul>
                                <li>Provide and improve the App’s functionality</li>
                                <li>Personalize your experience</li>
                                <li>Respond to support requests</li>
                                <li>Monitor performance and fix bugs</li>
                                <li>Ensure security and prevent misuse</li>
                                <li>Comply with legal obligations</li>
                            </ul>
                            <Typography variant="body1" paragraph>
                                We do not sell your personal information.
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>4. Sharing Your Information</Typography>
                            <Typography variant="body1" paragraph>
                                We may share information only in the following cases:
                            </Typography>
                            <Typography variant="subtitle1">4.1 Service Providers</Typography>
                            <Typography variant="body1" paragraph>
                                Trusted third‑party vendors may process data on our behalf (e.g., analytics, crash reporting, cloud hosting). They are required to protect your data and use it only for the services we request.
                            </Typography>
                            <Typography variant="subtitle1">4.2 Legal Requirements</Typography>
                            <Typography variant="body1" paragraph>
                                We may disclose information if required by law, regulation, or legal process.
                            </Typography>
                            <Typography variant="subtitle1">4.3 Business Transfers</Typography>
                            <Typography variant="body1" paragraph>
                                If we undergo a merger, acquisition, or asset sale, your information may be transferred as part of that transaction. You will be notified of any material changes.
                            </Typography>
                            <Typography variant="body1" paragraph>
                                We never share your data with advertisers or data brokers.
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>5. Data Retention</Typography>
                            <Typography variant="body1" paragraph>
                                We retain your information only as long as necessary to:
                            </Typography>
                            <ul>
                                <li>Provide the App’s services</li>
                                <li>Comply with legal obligations</li>
                                <li>Resolve disputes</li>
                                <li>Enforce agreements</li>
                            </ul>
                            <Typography variant="body1" paragraph>
                                You may request deletion of your data at any time (see Section 8).
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>6. Children’s Privacy</Typography>
                            <Typography variant="body1" paragraph>
                                We do not knowingly collect personal information from children under the age required by local law (typically 13). If we learn that a child has provided personal data, we will delete it promptly.
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>7. Security</Typography>
                            <Typography variant="body1" paragraph>
                                We implement reasonable administrative, technical, and physical safeguards to protect your information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute protection.
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>8. Your Rights</Typography>
                            <Typography variant="body1" paragraph>
                                Depending on your region, you may have the right to:
                            </Typography>
                            <ul>
                                <li>Access the information we hold about you</li>
                                <li>Request corrections</li>
                                <li>Request deletion</li>
                                <li>Withdraw consent</li>
                                <li>Disable data collection features (e.g., analytics)</li>
                                <li>Request a copy of your data</li>
                            </ul>
                            <Typography variant="body1" paragraph>
                                To exercise these rights, contact us at: <Link href="mailto:support@livingwaters.biz" color="secondary">support@livingwaters.biz</Link>
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>9. Third‑Party Services</Typography>
                            <Typography variant="body1" paragraph>
                                The App may link to or integrate with third‑party services. Their privacy practices are governed by their own policies, not ours. We encourage you to review them.
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>10. International Data Transfers</Typography>
                            <Typography variant="body1" paragraph>
                                If you use the App outside your home country, your information may be transferred to servers located in other jurisdictions. We take steps to ensure your data is protected according to applicable laws.
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>11. Changes to This Policy</Typography>
                            <Typography variant="body1" paragraph>
                                We may update this Privacy Policy from time to time. When we do, we will revise the “Last updated” date. Continued use of the App after changes means you accept the updated Policy.
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>12. Contact Us</Typography>
                            <Typography variant="body1" paragraph>
                                If you have questions about this Privacy Policy or our data practices, you can contact us at:
                            </Typography>
                            <Typography variant="body1" paragraph>
                                Email: <Link href="mailto:support@livingwaters.biz" color="secondary">support@livingwaters.biz</Link>
                            </Typography>
                        </Paper>
                    </Container>

                    <Footer />
                </Container>
            ) : (
                <>{loader}</>
            )}
        </>
    )
}

export default Privacy;
